import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Calculator, Printer, ChevronUp, Building2, BarChart2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BankaKredi {
    bankaKrediID: number;
    bankaAdi?: string;
    subeAdi?: string;
    telefon?: string;
    ilgiliKisi?: string;
    bankaLogo?: string;
    vadeOrani?: number;
}

interface OdemePlaniSatir {
    sira: number;
    taksit: number;
    anapara: number;
    faiz: number;
    bakiye: number;
}

interface HesapSonuc {
    taksitTutari: number;
    faizOrani: number;
    yillikMaliyetOrani: number;
    toplamOdeme: number;
    toplamFaiz: number;
    odemePlani: OdemePlaniSatir[];
}

const taksitSecenekleri = [12, 24, 36, 48, 60, 72, 84, 96, 120];

const formatPara = (val: number): string => {
    return val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const KrediHesaplama: React.FC = () => {
    const { t } = useTranslation();
    const [bankalar, setBankalar] = useState<BankaKredi[]>([]);
    const [seciliBanka, setSeciliBanka] = useState<BankaKredi | null>(null);
    const [krediTutari, setKrediTutari] = useState<string>('1.000.000');

    const formatBinlik = (val: string): string => {
        const raw = val.replace(/\./g, '');
        return raw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };
    const [taksitSayisi, setTaksitSayisi] = useState<string>('120');
    const [oran, setOran] = useState<string>('');
    const [sonuc, setSonuc] = useState<HesapSonuc | null>(null);
    const [odemePlaniAcik, setOdemePlaniAcik] = useState(true);

    const fetchBankalar = useCallback(async () => {
        try {
            const res = await axios.get('/api/BankaKredileri');
            setBankalar(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    }, []);

    useEffect(() => { fetchBankalar(); }, [fetchBankalar]);

    const handleBankaSecim = (banka: BankaKredi) => {
        setSeciliBanka(banka);
        if (banka.vadeOrani) {
            setOran(banka.vadeOrani.toString().replace('.', ','));
        }
        setSonuc(null);
    };

    const hesapla = () => {
        const tutar = parseFloat(krediTutari.replace(/\./g, '').replace(',', '.'));
        const taksit = parseInt(taksitSayisi);
        const aylikOranStr = oran.replace(',', '.');
        const aylikOran = parseFloat(aylikOranStr) / 100;

        if (isNaN(tutar) || tutar <= 0) { toast.warn('Geçerli bir kredi tutarı giriniz.'); return; }
        if (isNaN(taksit) || taksit <= 0) { toast.warn('Geçerli bir taksit sayısı giriniz.'); return; }
        if (isNaN(aylikOran) || aylikOran <= 0) { toast.warn('Geçerli bir oran giriniz.'); return; }

        // Aylık taksit hesaplama: T = P * r * (1+r)^n / ((1+r)^n - 1)
        const r = aylikOran;
        const n = taksit;
        const P = tutar;

        const taksitTutari = P * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);

        // Ödeme planı
        let bakiye = P;
        const plan: OdemePlaniSatir[] = [];

        for (let i = 1; i <= n; i++) {
            const faiz = bakiye * r;
            const anapara = taksitTutari - faiz;
            bakiye = bakiye - anapara;
            if (bakiye < 0) bakiye = 0;

            plan.push({
                sira: i,
                taksit: taksitTutari,
                anapara: anapara,
                faiz: faiz,
                bakiye: bakiye
            });
        }

        const toplamOdeme = taksitTutari * n;
        const toplamFaiz = toplamOdeme - P;

        // Yıllık maliyet oranı (basit yaklaşım): (1+r)^12 - 1
        const yillikMaliyetOrani = (Math.pow(1 + r, 12) - 1) * 100;

        setSonuc({
            taksitTutari,
            faizOrani: aylikOran * 100,
            yillikMaliyetOrani,
            toplamOdeme,
            toplamFaiz,
            odemePlani: plan
        });
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .kh-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 24px;
                    background: transparent;
                    min-height: 100vh;
                }
                .kh-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .kh-header-left { display: flex; flex-direction: column; gap: 3px; }
                .kh-title { font-size: 22px; font-weight: 700; color: #0f172a; margin: 0; }
                .kh-subtitle { font-size: 13px; color: #94a3b8; }

                /* Banka Kartları */
                .kh-bankalar {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                    margin-bottom: 28px;
                }
                .kh-banka-btn {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 22px;
                    background: #fff;
                    border: 2px solid #e2e8f0;
                    border-radius: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 220px;
                    flex: 0 0 auto;
                }
                .kh-banka-btn:hover {
                    border-color: #e67d21;
                    box-shadow: 0 4px 16px rgba(230,125,33,0.15);
                    transform: translateY(-2px);
                }
                .kh-banka-btn.kh-banka-aktif {
                    border-color: #1a2440;
                    background: linear-gradient(135deg, #f0f3fa 0%, #ffffff 100%);
                    box-shadow: 0 4px 20px rgba(26,36,64,0.15);
                }
                .kh-banka-logo {
                    width: 48px;
                    height: 48px;
                    border-radius: 10px;
                    object-fit: contain;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    flex-shrink: 0;
                }
                .kh-banka-logo-placeholder {
                    width: 48px;
                    height: 48px;
                    border-radius: 10px;
                    background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    flex-shrink: 0;
                }
                .kh-banka-info { display: flex; flex-direction: column; gap: 2px; text-align: left; }
                .kh-banka-adi { font-size: 14px; font-weight: 700; color: #0f172a; }
                .kh-banka-oran { font-size: 12px; color: #64748b; font-weight: 500; }
                .kh-banka-aktif .kh-banka-oran { color: #e67d21; font-weight: 700; }

                /* Split Layout */
                .kh-split {
                    display: grid;
                    grid-template-columns: 360px 1fr;
                    gap: 24px;
                    align-items: stretch;
                    margin-bottom: 24px;
                }
                @media (max-width: 900px) {
                    .kh-split { grid-template-columns: 1fr; }
                }

                /* Sol Panel - Form */
                .kh-form-card {
                    background: #fff;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                    padding: 24px;
                    height: 100%;
                    box-sizing: border-box;
                    display: flex;
                    flex-direction: column;
                }
                .kh-form-card-title {
                    font-size: 13px;
                    font-weight: 700;
                    color: #374151;
                    margin: 0 0 20px 0;
                    padding-bottom: 14px;
                    border-bottom: 1px solid #f3f4f6;
                }
                .kh-field {
                    margin-bottom: 16px;
                }
                .kh-field label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #6b7280;
                    margin-bottom: 6px;
                }
                .kh-field input,
                .kh-field select {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 14px;
                    font-family: inherit;
                    font-weight: 500;
                    color: #111827;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                    background: #fff;
                }
                .kh-field input:focus,
                .kh-field select:focus {
                    border-color: #1a2440;
                    box-shadow: 0 0 0 3px rgba(26,36,64,0.10);
                }
                .kh-hesapla-btn {
                    width: 100%;
                    padding: 12px;
                    background: #1a2440;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    transition: background 0.18s, box-shadow 0.18s;
                    margin-top: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    box-shadow: 0 2px 8px rgba(26,36,64,0.20);
                }
                .kh-hesapla-btn:hover { background: #e67d21; box-shadow: 0 4px 12px rgba(230,125,33,0.30); }

                /* Yazdır butonu */
                .kh-print-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 20px;
                    background: #fff;
                    color: #374151;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    font-family: inherit;
                    cursor: pointer;
                    transition: background 0.15s, box-shadow 0.15s;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                }
                .kh-print-btn:hover { background: #f9fafb; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }

                /* Input suffix wrapper */
                .kh-input-wrap {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .kh-input-wrap input {
                    padding-right: 36px;
                }
                .kh-input-suffix {
                    position: absolute;
                    right: 12px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #9ca3af;
                    pointer-events: none;
                    user-select: none;
                }

                /* Sağ Panel - Özet */
                .kh-ozet-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    height: 100%;
                    box-sizing: border-box;
                }
                .kh-ozet-main {
                    background: #fff;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                    padding: 24px;
                    animation: khFadeIn 0.3s ease;
                    flex: 1;
                    box-sizing: border-box;
                }
                .kh-ozet-empty {
                    background: #fff;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 48px 24px;
                    color: #9ca3af;
                    text-align: center;
                    gap: 12px;
                    flex: 1;
                }
                .kh-ozet-primary {
                    text-align: center;
                    padding: 20px 0 24px;
                    border-bottom: 1px solid #f3f4f6;
                    margin-bottom: 20px;
                }
                .kh-ozet-primary-label {
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #9ca3af;
                    margin-bottom: 6px;
                }
                .kh-ozet-primary-value {
                    font-size: 32px;
                    font-weight: 800;
                    color: #1a2440;
                    font-variant-numeric: tabular-nums;
                    letter-spacing: -0.5px;
                }
                .kh-ozet-primary-sub {
                    font-size: 13px;
                    color: #6b7280;
                    margin-top: 4px;
                }
                .kh-ozet-stats {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }
                .kh-ozet-stat {
                    background: #f9fafb;
                    border: 1px solid #f3f4f6;
                    border-radius: 10px;
                    padding: 14px 16px;
                }
                .kh-ozet-stat-label {
                    font-size: 11px;
                    color: #9ca3af;
                    font-weight: 500;
                    margin-bottom: 4px;
                }
                .kh-ozet-stat-value {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1a2440;
                    font-variant-numeric: tabular-nums;
                }
                .kh-ozet-stat-value.danger { color: #dc2626; }
                .kh-ozet-stat-value.info { color: #e67d21; }

                /* Sonuç Kartı */
                .kh-sonuc-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    padding: 28px;
                    margin-bottom: 24px;
                    animation: khFadeIn 0.3s ease;
                }
                @keyframes khFadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .kh-sonuc-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                    margin-bottom: 0;
                }
                .kh-sonuc-item {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 18px 20px;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .kh-sonuc-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    flex-shrink: 0;
                }
                .kh-sonuc-icon-green { background: #dcfce7; color: #16a34a; }
                .kh-sonuc-icon-blue { background: #dbeafe; color: #2563eb; }
                .kh-sonuc-icon-orange { background: #fff3e8; color: #e67d21; }
                .kh-sonuc-icon-red { background: #fef2f2; color: #dc2626; }
                .kh-sonuc-icon-purple { background: #f3e8ff; color: #7c3aed; }
                .kh-sonuc-text { display: flex; flex-direction: column; gap: 2px; }
                .kh-sonuc-label { font-size: 12px; color: #94a3b8; font-weight: 500; }
                .kh-sonuc-value { font-size: 18px; font-weight: 800; color: #1a2440; }

                /* Ödeme Planı */
                .kh-plan-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                    animation: khFadeIn 0.3s ease;
                }
                .kh-plan-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 28px;
                    cursor: pointer;
                    user-select: none;
                    background: #1a2440;
                    border-radius: 14px 14px 0 0;
                }
                .kh-plan-title {
                    font-size: 15px;
                    font-weight: 700;
                    color: #fff;
                    margin: 0;
                }
                .kh-plan-toggle {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    border: none;
                    background: rgba(255,255,255,0.15);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    transition: background 0.15s, transform 0.2s;
                    font-size: 14px;
                }
                .kh-plan-toggle:hover { background: rgba(255,255,255,0.25); }
                .kh-plan-toggle.kh-plan-acik { transform: rotate(180deg); }

                .kh-plan-table { width: 100%; border-collapse: collapse; }
                .kh-plan-table thead tr { background: #1a2440; }
                .kh-plan-table thead th {
                    padding: 12px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    color: rgba(255,255,255,0.75);
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    text-align: right;
                    white-space: nowrap;
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                }
                .kh-plan-table tbody tr {
                    border-bottom: 1px solid #f3f4f6;
                    transition: background 0.12s;
                }
                .kh-plan-table tbody tr:hover { background: #f0f3fa; }
                .kh-plan-table thead th:first-child { text-align: left; }
                .kh-plan-table tbody td {
                    padding: 12px 20px;
                    font-size: 13.5px;
                    color: #374151;
                    text-align: right;
                    font-weight: 500;
                    font-variant-numeric: tabular-nums;
                }
                .kh-plan-table tbody td:first-child {
                    text-align: left;
                    color: #1a2440;
                    font-weight: 700;
                }

                .kh-empty-hint {
                    text-align: center;
                    padding: 48px 20px;
                    color: #94a3b8;
                    font-size: 15px;
                }
                .kh-empty-hint i {
                    font-size: 40px;
                    display: block;
                    margin-bottom: 12px;
                    opacity: 0.4;
                }

                /* Print Styles */
                @media print {
                    body * { visibility: hidden; }
                    .kh-print-area, .kh-print-area * { visibility: visible; }
                    .kh-print-area {
                        position: absolute;
                        left: 0; top: 0;
                        width: 100%;
                        padding: 20px;
                    }
                    .kh-page { padding: 0 !important; background: #fff !important; }
                    .kh-bankalar, .kh-form-card, .kh-empty-hint, .kh-header, .kh-print-btn { display: none !important; }
                    .kh-print-header { display: flex !important; margin-bottom: 0; border-radius: 10px 10px 0 0; }
                    .kh-print-title { color: #fff !important; }
                    .kh-sonuc-card, .kh-plan-card { box-shadow: none !important; border: 1px solid #e2e8f0; }
                    .kh-plan-card { break-inside: auto; }
                    .kh-plan-table { font-size: 11px; }
                    .kh-plan-table thead th { padding: 8px 12px; }
                    .kh-plan-table tbody td { padding: 6px 12px; font-size: 11px; }
                    .kh-sonuc-grid { grid-template-columns: repeat(3, 1fr) !important; }
                    .kh-sonuc-item { padding: 12px !important; }
                    .kh-sonuc-value { font-size: 14px !important; }
                    .kh-plan-toggle { display: none !important; }
                }
                .kh-print-header {
                    display: none;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 0;
                    padding: 18px 24px;
                    background: #1a2440;
                    border-radius: 10px 10px 0 0;
                    color: #fff;
                }
                .kh-plan-footer {
                    padding: 14px 20px;
                    border-top: 1px solid #f3f4f6;
                    background: #f8fafc;
                    display: flex;
                    justify-content: space-between;
                    font-size: 12px;
                    color: #1a2440;
                    font-weight: 700;
                }
                .kh-print-title { font-size: 20px; font-weight: 800; color: #fff; margin: 0; }
                .kh-print-meta { font-size: 12px; color: rgba(255,255,255,0.7); text-align: right; line-height: 1.8; }
                .kh-print-meta strong { color: #e67d21; }
            `}</style>

            <div className="kh-page">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 40, gap: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a2440', letterSpacing: '-0.3px', margin: 0 }}>Kredi Hesaplama</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Kredi maliyetlerini, taksit tutarlarını ve ödeme planınızı detaylıca analiz edin.</p>
                    </div>
                    {sonuc && (
                        <button className="kh-print-btn" onClick={() => window.print()}>
                            <Printer size={15} />
                            Yazdır
                        </button>
                    )}
                </div>

                {/* Banka Seçimi */}
                {bankalar.length > 0 && (
                    <div className="kh-bankalar">
                        {bankalar.map(b => (
                            <div
                                key={b.bankaKrediID}
                                className={`kh-banka-btn${seciliBanka?.bankaKrediID === b.bankaKrediID ? ' kh-banka-aktif' : ''}`}
                                onClick={() => handleBankaSecim(b)}
                            >
                                {b.bankaLogo ? (
                                    <img
                                        src={`${axios.defaults.baseURL}/logos/${b.bankaLogo}`}
                                        alt={b.bankaAdi || 'Logo'}
                                        className="kh-banka-logo"
                                    />
                                ) : (
                                    <div className="kh-banka-logo-placeholder"><Building2 size={22} color="#60a5fa" /></div>
                                )}
                                <div className="kh-banka-info">
                                    <span className="kh-banka-adi">{b.bankaAdi || 'Banka'}</span>
                                    <span className="kh-banka-oran">
                                        {b.vadeOrani ? `% ${b.vadeOrani.toString().replace('.', ',')}` : 'Oran belirtilmemiş'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Split Layout */}
                <div className="kh-split">
                    {/* Sol Panel - Form */}
                    <div className="kh-form-card">
                        <p className="kh-form-card-title">Giriş Parametreleri</p>
                        <div className="kh-field">
                            <label>Kredi Tutarı</label>
                            <div className="kh-input-wrap">
                                <input
                                    type="text"
                                    value={krediTutari}
                                    onChange={e => {
                                        const raw = e.target.value.replace(/[^0-9]/g, '');
                                        setKrediTutari(formatBinlik(raw));
                                    }}
                                    placeholder="1.000.000"
                                />
                                <span className="kh-input-suffix">₺</span>
                            </div>
                        </div>
                        <div className="kh-field">
                            <label>Taksit Sayısı</label>
                            <div className="kh-input-wrap">
                                <input
                                    type="text"
                                    list="taksit-secenekleri"
                                    value={taksitSayisi}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        setTaksitSayisi(val);
                                    }}
                                    placeholder="120"
                                />
                                <span className="kh-input-suffix">Ay</span>
                            </div>
                            <datalist id="taksit-secenekleri">
                                {taksitSecenekleri.map(item => (
                                    <option key={t} value={item.toString()} />
                                ))}
                            </datalist>
                        </div>
                        <div className="kh-field">
                            <label>Aylık Faiz Oranı</label>
                            <div className="kh-input-wrap">
                                <input
                                    type="text"
                                    value={oran}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9.,]/g, '');
                                        setOran(val);
                                    }}
                                    placeholder="2,59"
                                />
                                <span className="kh-input-suffix">%</span>
                            </div>
                        </div>
                        <button className="kh-hesapla-btn" onClick={hesapla}>
                            <Calculator size={15} style={{ marginRight: 6 }} />
                            Hesapla
                        </button>
                    </div>

                    {/* Sağ Panel - Özet */}
                    <div className="kh-ozet-panel">
                        {!sonuc ? (
                            <div className="kh-ozet-empty">
                                <BarChart2 size={32} style={{ opacity: 0.3 }} />
                                <span>Parametreleri doldurun ve<br/>"Hesapla" butonuna tıklayın.</span>
                            </div>
                        ) : (
                            <div className="kh-ozet-main">
                                <div className="kh-ozet-primary">
                                    <div className="kh-ozet-primary-label">Aylık Taksit Tutarı</div>
                                    <div className="kh-ozet-primary-value">{formatPara(sonuc.taksitTutari)} <span style={{ fontSize: 18, fontWeight: 600, color: '#6b7280' }}>TL</span></div>
                                    <div className="kh-ozet-primary-sub">{taksitSayisi} ay &times; {formatPara(sonuc.taksitTutari)} TL</div>
                                </div>
                                <div className="kh-ozet-stats">
                                    <div className="kh-ozet-stat">
                                        <div className="kh-ozet-stat-label">Toplam Geri Ödeme</div>
                                        <div className="kh-ozet-stat-value">{formatPara(sonuc.toplamOdeme)} TL</div>
                                    </div>
                                    <div className="kh-ozet-stat">
                                        <div className="kh-ozet-stat-label">Toplam Faiz Maliyeti</div>
                                        <div className="kh-ozet-stat-value danger">{formatPara(sonuc.toplamFaiz)} TL</div>
                                    </div>
                                    <div className="kh-ozet-stat">
                                        <div className="kh-ozet-stat-label">Aylık Faiz Oranı</div>
                                        <div className="kh-ozet-stat-value info">% {sonuc.faizOrani.toFixed(2).replace('.', ',')}</div>
                                    </div>
                                    <div className="kh-ozet-stat">
                                        <div className="kh-ozet-stat-label">Yıllık Maliyet Oranı</div>
                                        <div className="kh-ozet-stat-value">% {sonuc.yillikMaliyetOrani.toFixed(2).replace('.', ',')}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Ödeme Planı Tablosu - full width */}
                {sonuc && (
                    <div className="kh-print-area">
                        <div className="kh-print-header">
                            <div>
                                <h1 className="kh-print-title">
                                    {seciliBanka ? seciliBanka.bankaAdi : 'Kredi'} - Ödeme Planı
                                </h1>
                            </div>
                            <div className="kh-print-meta">
                                <div>Kredi Tutarı: <strong>{formatPara(parseFloat(krediTutari.replace(/\./g, '').replace(',', '.')) || 0)} TL</strong></div>
                                <div>Taksit: <strong>{taksitSayisi} ay</strong> &nbsp;|&nbsp; Oran: <strong>% {sonuc.faizOrani.toFixed(2).replace('.', ',')}</strong></div>
                                <div>Tarih: <strong>{new Date().toLocaleDateString('tr-TR')}</strong></div>
                            </div>
                        </div>
                        <div className="kh-plan-card">
                            <div className="kh-plan-header" onClick={() => setOdemePlaniAcik(!odemePlaniAcik)}>
                                <h2 className="kh-plan-title">Ödeme Planı</h2>
                                <button className={`kh-plan-toggle${odemePlaniAcik ? ' kh-plan-acik' : ''}`}>
                                    <ChevronUp size={14} />
                                </button>
                            </div>
                            {odemePlaniAcik && (
                                <>
                                    <table className="kh-plan-table">
                                        <thead>
                                            <tr>
                                                <th>Sıra</th>
                                                <th>Taksit</th>
                                                <th>Anapara</th>
                                                <th>Faiz</th>
                                                <th>Bakiye</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sonuc.odemePlani.map(s => (
                                                <tr key={s.sira}>
                                                    <td>{s.sira}</td>
                                                    <td>{formatPara(s.taksit)}</td>
                                                    <td>{formatPara(s.anapara)}</td>
                                                    <td>{formatPara(s.faiz)}</td>
                                                    <td>{formatPara(s.bakiye)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="kh-plan-footer">
                                        <span>Toplam {sonuc.odemePlani.length} taksit</span>
                                        <span>Toplam Faiz: {formatPara(sonuc.toplamFaiz)} TL</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default KrediHesaplama;
